import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectDarkMode } from '../../../app/store/slices/uiSlice';
import { ChildrenService } from '../../../shared/services/childrenService';
import { ArrowLeftIcon, CalendarIcon, UserIcon, MapPinIcon, ClipboardDocumentCheckIcon, BeakerIcon } from '@heroicons/react/24/outline';
import {
  Table,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TableEmptyState,
  Pagination,
  TableBodySkeleton
} from '../../../shared/components/ui';
import { StatusBadge } from '../../../shared/components/badges/StatusBadge';

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export default function ChildVaccinationRecordsPage() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const darkMode = useSelector(selectDarkMode);

  const [records, setRecords] = useState([]);
  const [childInfo, setChildInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1
  });

  useEffect(() => {
    fetchVaccinationRecords();
  }, [childId, pagination.page]);

  const fetchVaccinationRecords = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ChildrenService.getVaccinationRecords(childId, {
        page: pagination.page,
        limit: pagination.limit
      });

      const data = response?.data || response;
      setRecords(data || []);

      // Get child info from first record if available
      if (data?.length > 0 && data[0].child) {
        setChildInfo(data[0].child);
      }

      // Update pagination from response
      if (response?.pagination) {
        setPagination(prev => ({
          ...prev,
          total: response.pagination.total || 0,
          totalPages: response.pagination.totalPages || 1
        }));
      }
    } catch (err) {
      console.error('Failed to fetch vaccination records:', err);
      setError('Failed to load vaccination records. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleGoBack = () => {
    navigate('/admin/children');
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={handleGoBack}
            className={`flex items-center gap-2 mb-4 px-3 py-2 rounded-lg transition-colors ${
              darkMode
                ? 'text-gray-300 hover:bg-gray-800'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <ArrowLeftIcon className="w-5 h-5" />
            <span>Back to Children</span>
          </button>

          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
              <BeakerIcon className={`w-8 h-8 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            </div>
            <div>
              <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Vaccination Records
              </h1>
              {childInfo && (
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {childInfo.name} • {childInfo.gender} • DOB: {formatDate(childInfo.dateOfBirth)}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Child Info Card */}
        {childInfo && (
          <div className={`mb-6 p-4 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
            <div className="flex items-center gap-4">
              {childInfo.profilePhoto ? (
                <img
                  src={childInfo.profilePhoto}
                  alt={childInfo.name}
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center">
                  <span className="text-2xl">👶</span>
                </div>
              )}
              <div className="flex-1">
                <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {childInfo.name}
                </h2>
                {childInfo.parent && (
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Parent: {childInfo.parent.fullName} • {childInfo.parent.phone}
                  </p>
                )}
              </div>
              <div className={`text-right ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                <p className="text-sm">Total Records: <span className="font-semibold">{pagination.total}</span></p>
              </div>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-300 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Records Table - Using Reusable Table Components */}
        <Table>
          <TableHead>
            <tr>
              <TableHeaderCell>Vaccine</TableHeaderCell>
              <TableHeaderCell>Dose</TableHeaderCell>
              <TableHeaderCell>Administered Date</TableHeaderCell>
              <TableHeaderCell>Administered By</TableHeaderCell>
              <TableHeaderCell>Center</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
            </tr>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableBodySkeleton rows={5} columns={6} />
            ) : records.length > 0 ? (
              records.map((record) => (
                <TableRow key={record.id}>
                  <TableCell>
                    <div>
                      <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {record.vaccine?.name || '—'}
                      </p>
                      <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Batch: {record.batchNumber || '—'}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      darkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {record.doseLabel || `Dose ${record.doseNumber}`}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <CalendarIcon className="w-4 h-4 text-gray-400" />
                      {formatDateTime(record.administeredDate)}
                    </div>
                    <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      Age: {record.ageAtVaccination || '—'}
                    </p>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                    
                          <UserIcon className="w-4 h-4 text-gray-400" />
                       
                      
                      <div>
                        <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {record.administeredBy?.name || '—'}
                        </p>
                        <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {record.administeredBy?.specialization || record.administeredBy?.role || '—'}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <MapPinIcon className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {record.vaccinationCenter?.name || '—'}
                        </p>
                        <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {record.vaccinationCenter?.phone || '—'}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={record.appointment?.status || 'COMPLETED'} />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableEmptyState
                icon={ClipboardDocumentCheckIcon}
                title="No vaccination records found"
                message="This child has no vaccination records yet."
              />
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              totalItems={pagination.total}
              itemsPerPage={pagination.limit}
              onPageChange={handlePageChange}
              itemName="record"
              isLoading={loading}
            />
          </div>
        )}

        {/* Record Details - Expanded View for each record */}
        {!loading && records.length > 0 && (
          <div className="mt-6 space-y-4">
            <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Record Details
            </h3>
            {records.map((record) => (
              <div
                key={record.id}
                className={`p-4 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {record.vaccine?.name} - {record.doseLabel}
                    </h4>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {formatDateTime(record.administeredDate)}
                    </p>
                  </div>
                  <StatusBadge status={record.appointment?.status || 'COMPLETED'} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <p className={`text-xs font-medium ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Vaccine Info</p>
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {record.vaccine?.description || '—'}
                    </p>
                    <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      Dosage: {record.vaccine?.dosage || '—'}
                    </p>
                  </div>

                  <div>
                    <p className={`text-xs font-medium ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Batch & Schedule</p>
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Batch: {record.batchNumber || '—'}
                    </p>
                    {record.nextDueDate && (
                      <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        Next Due: {formatDate(record.nextDueDate)}
                      </p>
                    )}
                  </div>

                  <div>
                    <p className={`text-xs font-medium ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Appointment</p>
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {formatDate(record.appointment?.scheduledDate)} at {record.appointment?.scheduledTime || '—'}
                    </p>
                  </div>

                  {record.reactions && (
                    <div>
                      <p className={`text-xs font-medium ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Reactions</p>
                      <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {record.reactions}
                      </p>
                    </div>
                  )}

                  {record.notes && (
                    <div className="md:col-span-2">
                      <p className={`text-xs font-medium ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Notes</p>
                      <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {record.notes}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
